import { useEffect, useRef, useState, type ReactNode } from 'react';
import { X, Camera, Keyboard } from 'lucide-react';
import type { IScannerControls } from '@zxing/browser';

interface BarcodeScannerProps {
  /**
   * Called with each code read. `source` says whether the camera saw it (it
   * fires on every frame the code is in view, so a parent that counts scans
   * must ignore repeats) or it was typed or sent by a USB scanner.
   */
  onScan: (barcode: string, source: 'camera' | 'manual') => void;
  onClose: () => void;
  label?: string;
  /** Open on manual entry: where a USB or Bluetooth scanner types the code. */
  startManual?: boolean;
  /** Shown under the camera or the entry box, e.g. what the last scan did. */
  children?: ReactNode;
}

export default function BarcodeScanner({ onScan, onClose, label, startManual = false, children }: BarcodeScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [manualMode, setManualMode] = useState(startManual);
  const [manualValue, setManualValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const controlsRef = useRef<IScannerControls | null>(null);

  // Keep the latest onScan in a ref so the effect below doesn't depend on it.
  // Parents typically pass an inline arrow, which would otherwise tear down and
  // restart the camera on every render.
  const onScanRef = useRef(onScan);
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  useEffect(() => {
    if (manualMode) return;

    let active = true;

    function stopScanner() {
      if (controlsRef.current) {
        try { controlsRef.current.stop(); } catch { /* already stopped */ }
        controlsRef.current = null;
      }
    }

    async function startScanner() {
      try {
        const { BrowserMultiFormatReader } = await import('@zxing/browser');
        if (!active || !videoRef.current) return;

        const codeReader = new BrowserMultiFormatReader();
        setScanning(true);

        // decodeFromVideoDevice returns IScannerControls — .stop() is what releases
        // the MediaStream. The reader itself has no reset() in @zxing/browser v0.1.x.
        const controls = await codeReader.decodeFromVideoDevice(
          undefined, // undefined = let zxing pick, preferring the environment-facing camera
          videoRef.current,
          (result) => {
            if (!active || !result) return;
            onScanRef.current(result.getText(), 'camera');
            // Don't stop here — the parent decides whether to close.
          }
          // Decode errors (NotFoundException) fire every frame with no barcode in
          // view, so they're deliberately ignored.
        );

        if (!active) {
          // Unmounted while the camera was starting up — release it immediately.
          try { controls.stop(); } catch { /* ignore */ }
          return;
        }
        controlsRef.current = controls;
      } catch (e: unknown) {
        if (!active) return;
        const msg = e instanceof Error ? e.message : String(e);
        if (msg.includes('Permission') || msg.includes('NotAllowed')) {
          setError('Camera permission denied. Use manual entry instead.');
        } else {
          setError('Camera unavailable. Use manual entry instead.');
        }
        setScanning(false);
        setManualMode(true);
      }
    }

    startScanner();

    return () => {
      active = false;
      stopScanner();
    };
  }, [manualMode]);

  function handleManualSubmit(e: React.FormEvent) {
    e.preventDefault();
    const val = manualValue.trim();
    if (val) {
      onScanRef.current(val, 'manual');
      setManualValue('');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden max-h-full overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">
            {label ?? 'Scan Barcode'}
          </h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setManualMode(m => !m)}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              {manualMode ? (
                <><Camera className="h-3.5 w-3.5" /> Use Camera</>
              ) : (
                <><Keyboard className="h-3.5 w-3.5" /> Enter Manually</>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-4">
          {error && (
            <p className="text-sm text-red-600 mb-3 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}

          {!manualMode ? (
            <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                autoPlay
                muted
                playsInline
              />
              {/* Targeting overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="border-2 border-green-400 rounded w-2/3 h-1/3 opacity-70" />
              </div>
              {!scanning && !error && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <p className="text-white text-sm">Starting camera...</p>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleManualSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Enter barcode / serial number
                </label>
                <input
                  type="text"
                  value={manualValue}
                  onChange={e => setManualValue(e.target.value)}
                  placeholder="Scan or type barcode..."
                  autoFocus
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                type="submit"
                disabled={!manualValue.trim()}
                className="w-full bg-blue-600 text-white text-sm font-medium py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit
              </button>
            </form>
          )}
        </div>

        {!manualMode && (
          <p className="text-center text-xs text-gray-400 pb-3">
            Point camera at a barcode to scan automatically
          </p>
        )}

        {children && <div className="px-4 pb-4">{children}</div>}
      </div>
    </div>
  );
}
